import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  CustomizationEncourageChangesModal,
  CustomizationOutOfStockModal,
  CustomizationRequiredModificationModal,
  useCustomizationContext,
  useCustomizationHelpers,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { type useCustomizationContent } from '../../../../hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const CustomizationModals = (props: Props) => {
  const {
    actor,
    customizationContent,
    isOutOfStockModalOpen,
    closeOutOfStockModal,
    setScrollTargetId,
  } = props;

  const { track } = useTelemetry();

  // ─────────────────────────────────────────────────────────────────────

  const {
    outOfStockIngredients,
    product,
    restaurant,
    shouldShowEncourageChangesModal,
    encouragedChangesList,
    shouldShowRequiredModificationModal,
    requiredIngredientName,
  } = useCustomizationContext({ actor });

  const { continueCustomization, selectDetails } = useCustomizationHelpers({
    actor,
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const trackEncourageChangesModalView = useCallback(() => {
    track('customization.encourage-changes-modal.shown', {
      encouragedChangeEntry: encouragedChangesList[0],
      selectedProduct: telemetryProductFromInput(product),
    });
  }, [encouragedChangesList, product, track]);

  const trackRequiredModificationModalView = useCallback(() => {
    track('customization.required-modification-modal.shown', {
      requiredIngredientName,
      selectedProduct: telemetryProductFromInput(product),
    });
  }, [product, requiredIngredientName, track]);

  // ─────────────────────────────────────────────────────────────────────

  if (!product || !restaurant) {
    return null;
  }

  return (
    <View style={styles.wrapper}>
      <CustomizationOutOfStockModal
        isVisible={isOutOfStockModalOpen}
        onRequestClose={closeOutOfStockModal}
        outOfStockIngredients={outOfStockIngredients}
        productName={product.name}
        restaurantName={restaurant.name}
        singleIngredientBody={customizationContent.fields?.outOfStockSingleIngredientBody}
        multipleIngredientsBody={customizationContent.fields?.outOfStockMultipleIngredientsBody}
        singleIngredientButtonTitle={
          customizationContent.fields?.outOfStockSingleIngredientButtonTitle
        }
        multipleIngredientsButtonTitle={
          customizationContent.fields?.outOfStockMultipleIngredientsButtonTitle
        }
        onChooseNewIngredientPress={setScrollTargetId}
      />

      {encouragedChangesList[0] ? (
        <CustomizationEncourageChangesModal
          isVisible={shouldShowEncourageChangesModal}
          entry={encouragedChangesList[0]}
          size="md"
          heading={customizationContent.fields?.encourageChangesHeading}
          baseHeading={customizationContent.fields?.encourageChangesBaseHeading}
          noSelectionHeading={customizationContent.fields?.encourageChangesNoSelectionHeading}
          body={customizationContent.fields?.encourageChangesBody}
          baseBody={customizationContent.fields?.encourageChangesBaseBody}
          continueButtonTitle={customizationContent.fields?.encourageChangesContinueButtonTitle}
          goBackButtonTitle={customizationContent.fields?.encourageChangesGoBackButtonTitle}
          onRequestClose={continueCustomization}
          onAddMore={continueCustomization}
          onContinue={selectDetails}
          onShow={trackEncourageChangesModalView}
        />
      ) : null}

      <CustomizationRequiredModificationModal
        isVisible={shouldShowRequiredModificationModal}
        productName={product.name}
        requiredIngredientName={requiredIngredientName}
        size="md"
        body={customizationContent.fields?.requiredModificationBody}
        buttonTitle={customizationContent.fields?.requiredModificationButtonTitle}
        onConfirm={continueCustomization}
        onShow={trackRequiredModificationModalView}
      />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flex: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  customizationContent: ReturnType<typeof useCustomizationContent>;
  isOutOfStockModalOpen: boolean;
  closeOutOfStockModal: () => void;
  setScrollTargetId?: (id: string) => void;
};
